import React, { PropsWithChildren } from 'react';
import Link, { LinkProps } from 'next/link';
import { logEventAsArray, TAnalyticArgs } from '@/analytics';
import { Badge, IBadgeProps } from '@mofid-securities/uikit';
import { Icon, IIconProps } from '@/components/atomic/Icon';
import { classnames } from '@/utils/classnames';

export interface ICardLinkProps
  extends Pick<TAnchorProps, 'target'>,
    Pick<TDivProps, 'id' | 'className' | 'onClick'> {
  href?: LinkProps['href'];
  badgeNumber?: number;
  analytics?: TAnalyticArgs;
  classNames?: Partial<Record<TCardLinkParts, string>>;
  iconProps?: Partial<Omit<IIconProps, 'ref'>>;
  badgeProps?: IBadgeProps;
  isClickIconVisible?: boolean;
}

type TCardLinkParts = 'root' | 'badge' | 'icon';

export const CardLink = ({
  id,
  href,
  iconProps = { className: undefined },
  badgeProps = { className: undefined },
  badgeNumber,
  children,
  analytics,
  target,
  className,
  classNames,
  onClick,
  isClickIconVisible = true,
}: PropsWithChildren<ICardLinkProps>) => {
  const handleClick: TDivProps['onClick'] = (...args) => {
    logEventAsArray(analytics);
    if (onClick) onClick(...args);
  };

  const { className: iconClassName, ...restIconProps } = iconProps;
  const { className: badgeClassName, ...restBadgeProps } = badgeProps;

  const isInnerContentVisible = isClickIconVisible || !!badgeNumber;

  const content = (
    <div
      className={classnames(
        'flex w-full cursor-pointer flex-row items-center justify-between rounded-xl border border-solid border-gray-20 px-4 py-3 text-sm font-medium text-gray-120',
        className,
        classNames?.root
      )}
      id={id}
      onClick={handleClick}
    >
      {children}
      {isInnerContentVisible && (
        <div className="flex flex-row items-center gap-x-1">
          {!!badgeNumber && (
            <Badge
              className={classnames(
                'bg-blue-a20 !p-0.5',
                classNames?.badge,
                badgeClassName
              )}
              {...restBadgeProps}
            >
              {badgeNumber}
            </Badge>
          )}
          {isClickIconVisible && (
            <Icon
              className={classnames(classNames?.icon, iconClassName)}
              color="blue-50"
              icon="chevron_left"
              size={24}
              {...restIconProps}
            />
          )}
        </div>
      )}
    </div>
  );

  return href ? (
    <Link
      href={href}
      target={target}
      {...(target === '_blank' && { rel: 'noopener noreferrer' })}
    >
      {content}
    </Link>
  ) : (
    content
  );
};
